/**
 * SidebarFolderItem - Recursive folder tree item for share sidebar navigation
 *
 * Used in SharePage to display folder structure in the sidebar.
 * Supports expand/collapse, selection highlighting, and document counts.
 */

import { useState } from "react";
import { ChevronRight, ChevronDown, FolderOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { getTotalDocCount } from "@/stores/shares";
import type { ShareFolderNode } from "@/stores/shares";

interface SidebarFolderItemProps {
  folder: ShareFolderNode;
  currentFolderId: string | null;
  onFolderSelect: (folderId: string | null) => void;
  depth?: number;
}

/**
 * Sidebar folder tree item for navigation
 */
export function SidebarFolderItem({
  folder,
  currentFolderId,
  onFolderSelect,
  depth = 0,
}: SidebarFolderItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const isSelected = currentFolderId === folder.id;
  const hasChildren = folder.children.length > 0;
  const totalDocCount = getTotalDocCount(folder);

  return (
    <div>
      <div
        className={cn(
          "group flex min-w-0 items-center gap-1 rounded-md py-1 pr-2 text-sm transition-colors",
          isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted"
        )}
        // Indent per nesting depth so sub-subfolders sit below their parent
        // rather than level with their parent's siblings (matches the
        // projects folder tree, which indents by depth).
        style={{ paddingLeft: `${depth * 18 + 8}px` }}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-6" />
        )}

        {/* Folder button */}
        <button
          onClick={() => onFolderSelect(folder.id)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <FolderOpen
            className={cn(
              "h-4 w-4 shrink-0",
              isSelected ? "text-primary" : "text-muted-foreground"
            )}
          />
          <span className="min-w-0 flex-1 truncate">{folder.name}</span>
          {totalDocCount > 0 && (
            <span className="shrink-0 text-xs text-muted-foreground">{totalDocCount}</span>
          )}
        </button>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children.map((child) => (
            <SidebarFolderItem
              key={child.id}
              folder={child}
              currentFolderId={currentFolderId}
              onFolderSelect={onFolderSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
